import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { GroupService } from './../../../core/services/group.service';
import { UserService } from './../../../core/services/user.service';
//import { TutorialService } from 'src/app/core/tutorial/tutorial.service';
import { TutorialSection } from 'src/app/core/tutorial/tutorial.models';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { referralsEnabledFor } from '../../../core/services/referral.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
  selector: 'app-settings-container',
  templateUrl: './settings-container.component.html',
  styleUrls: ['./settings-container.component.scss'],
  animations: [
    trigger('tabAnimation', [
      transition(':enter', [
        query('.mat-mdc-tab', [
          style({ opacity: 0, transform: 'translateX(100%)' }),
          stagger(500, [
            animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
          ])
        ])
      ])
    ])
  ]
})
export class SettingsContainerComponent extends BaseComponent implements OnInit {

  /**
   * Practice forms are BUILT AND HARNESSED but hidden from the UI (tasks/patient-forms.md §17).
   * Read once here rather than reaching into `environment` from the template, which the AOT
   * template checker cannot see through.
   */
  /**
   * Practice forms are BUILT AND HARNESSED but hidden from the UI (tasks/patient-forms.md §17).
   *
   * Read through a cast, deliberately. The real `environment.*.ts` files are GITIGNORED — only the
   * samples are tracked — so a deployer's existing `environment.prod.ts` will not carry this key
   * until someone adds it, and a direct property access would fail the strict build rather than
   * hide a feature. The cast makes a missing key mean `undefined`, and `=== true` makes `undefined`
   * mean HIDDEN. Fail-closed: the only way this feature appears is if someone deliberately sets it.
   */
  /**
   * The rendered tab order, and the only place it is written down.
   *
   * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────────────
   *
   * `mat-tab-group` selects by INDEX, and the indices here are not stable: nine of the twelve
   * tabs are `*ngIf="isAdmin()"`, Forms is additionally behind `practiceFormsEnabled`, and the
   * last is `isAdmin() || newSubscription`. So the index of a given tab depends on WHO is
   * looking, and it moves whenever a tab is added.
   *
   * It moved on 8 Sep 2026, when Documents was inserted after Consent. That shifted every later
   * tab by one and broke `multiline-items.component.ts`, which navigated to `/settings?tab=5`
   * expecting Coding and would have landed on Survey Template. Two hard-coded `selectedTabId = 7`
   * assignments in this file were already wrong before that — they were meant to open
   * Subscriptions & Usage and did not.
   *
   * A magic number cannot be right for both an admin and a non-admin at once, so callers name
   * the tab instead and `indexOfTab` resolves it against what is actually rendered.
   *
   * ⚠ THIS LIST MUST MIRROR THE TEMPLATE, IN ORDER, INCLUDING THE `*ngIf`. A tab added to the
   * template and not to this list silently shifts every key below it — the exact defect above.
   * `Subscription & Tokens` is commented out in the template and is therefore absent here.
   */
  private get tabs(): { key: string; visible: () => boolean }[] {
    return [
      { key: 'profile', visible: () => true },
      { key: 'notes', visible: () => this.isAdmin() },
      { key: 'schedule', visible: () => this.isAdmin() },
      { key: 'consent', visible: () => this.isAdmin() },
      { key: 'documents', visible: () => this.isAdmin() },
      { key: 'forms', visible: () => this.practiceFormsEnabled && this.isAdmin() },
      { key: 'survey', visible: () => this.isAdmin() && !this.stateMode },
      { key: 'coding', visible: () => this.isAdmin() && !this.stateMode },
      { key: 'tariffs', visible: () => this.isAdmin() && !this.stateMode },
      { key: 'network', visible: () => this.isAdmin() },
      { key: 'subscriptions', visible: () => this.isAdmin() || !!this.newSubscription },
    ];
  }

  /** The rendered index of a named tab, or 0 when it is not rendered for this user. */
  indexOfTab(key: string): number {
    // The Referral Network tab became part of MedicAI Network (2026-09-18): an old `?tab=referrals` link still lands there.
    if (key === 'referrals') key = 'network';
    const index = this.tabs.filter(t => t.visible()).findIndex(t => t.key === key);
    return index < 0 ? 0 : index;
  }

  get referralsEnabled(): boolean {
    return referralsEnabledFor(this.groupService.group);
  }

  /** A state unit (tasks/state-mode.md): no surveys, no procedure coding, no tariffs or payments, no referrals. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  readonly practiceFormsEnabled =
    (environment as Record<string, unknown>)['PRACTICE_FORMS_ENABLED'] === true;

  userRole?: 'Admin' | 'Physician' | 'Support-Limited' | 'Support-Unlimited';
  userType?: 'doctor' | 'support';
  newSubscription: boolean = false;
  selectedTabId: number = 0;
  isNative: boolean = (environment as any).isNative === true;

  constructor(
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private groupService: GroupService,
    //private tutorialService: TutorialService,
    private cd: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          this.userType = user.userType;
        }
      })

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedGroup => {
        if (selectedGroup) {
          this.fetchGroupUsers();
        }
      });

    this.groupService.processNewSubscription
      .pipe(takeUntil(this.destroy$))
      .subscribe(newSubscription => {
        this.newSubscription = newSubscription;
        if (newSubscription) {
          setTimeout(() => {
            //this.tutorialService.closeTutorial();
            // Was a hard-coded 7, which landed on the wrong tab for an admin even before
            // Documents shifted the indices. Named, so it follows the template.
            this.selectedTabId = this.indexOfTab('subscriptions');
          }, 1000);
        }
      });

    this.groupService.updateCardDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe(updateCardDetails => {
        if (updateCardDetails) {
          setTimeout(() => {
            //this.tutorialService.closeTutorial();
            // Was a hard-coded 7, which landed on the wrong tab for an admin even before
            // Documents shifted the indices. Named, so it follows the template.
            this.selectedTabId = this.indexOfTab('subscriptions');
          }, 1000);
        }
      });


  }

  ngOnInit() {
    this.fetchGroupUsers();

    this.onTabChange(0);

    //get data from url
    this.activatedRoute.queryParams.subscribe(params => {
      //console.log("Params: ", params);
      if (params['tab']) {
        setTimeout(() => {
          //this.tutorialService.closeTutorial();
          // A NAME resolves against what is actually rendered; a NUMBER is taken as a raw index
          // for the links that predate `indexOfTab` and for anything outside this app. Prefer the
          // name: an index is only correct for the user it was written against, and it moves
          // whenever a tab is added — see the note on `tabs` above.
          const requested = String(params['tab']);
          this.selectedTabId = /^\d+$/.test(requested)
            ? (Number(requested) || 0)
            : this.indexOfTab(requested);
        }, 1000);
      }
    });


    //Subscribing to tab change observable to update the tab
    /* this.tutorialService.tabChange$.subscribe((tabIndex) => {
      if (tabIndex !== null) {
        this.selectedTabId = tabIndex; //This will update the active tab
      }
    }); */
  }

  override ngOnDestroy(): void {
    //this.tutorialService.closeTutorial();
  }

  fetchGroupUsers() {
    this.groupService.getGroupUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (results) => {
          const currentGroupUser = results.users?.filter((u: any) => u.userId === this.userService.user.id);
          this.userRole = currentGroupUser[0].userRole;
          this.cd.detectChanges();
        },
        (error) => {
          console.error('Error fetching group users:', error);
        }
      );
  }

  onTabChange(index: number) {
    //Is responsible for starting the tutorial based on the open tab
    /* this.tutorialService.fetchHiddenSlides();
    switch (index) {
      case 0:
        this.tutorialService.startTutorial(TutorialSection.PROFILE_SETTINGS, true);
        break;
      case 1:
        this.tutorialService.startTutorial(TutorialSection.NOTE_SETTINGS, true);
        break;
      case 2:
        this.tutorialService.startTutorial(TutorialSection.SCHEDULE_SETTINGS, true);
        break;
      case 3:
        this.tutorialService.startTutorial(TutorialSection.CONSENT_SETTINGS, true);
        break;
      case 5:
        this.tutorialService.startTutorial(TutorialSection.CODING_SETTINGS, true);
        break;
      case 7:
        this.tutorialService.startTutorial(TutorialSection.NETWORK_SETTINGS, true);
        break;
      // case 6: Last slide to se
      //   this.tutorialService.startTutorial(TutorialSection.SUPPORT_STAFF, true);
      //   break;
      default:
        break;
    } */
    
  }

  isAdmin() {
    return this.userRole?.toLowerCase() === 'admin';
  }

  isGroupUser() {
    if (!this.userRole) return false;
    return !this.isAdmin();
  }

}
